import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Row } from 'react-bootstrap'
import {
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Link2,
  Sparkles,
} from 'lucide-react'
import { cn } from '@/features/utils'
import { sendGtmPixel, pickPrimaryStoreAccount } from '../../../modules/tagManager'
import { selectRootAccounts, selectRootUser } from '../../../rootSlice'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/features/shadcn/ui/dialog'
import { Input } from '@/features/shadcn/ui/input'
import { Button } from '@/features/shadcn/ui/button'
import { planOptions } from '../../../applications/Plan/planConfig'
import PricingPlanCard from '../../../applications/Plan/components/PricingPlanCard'
import { linkFromChannel } from '../../../modules/account'
import {
  CREATE_ORG_WIZARD_RESUME_KEY,
  createOrganization,
  fetchOrganizations,
  provisionOrganizationTenancy,
} from '../../../features/organizations/organizationsSlice'
import CreateOrganizationCheckout from './CreateOrganizationCheckout'
import ShopifyLogo from '../../../assets/images/logos/shopify.svg'
import BigcommerceLogo from '../../../assets/images/logos/bigcommerce.svg'
import WoocommerceLogo from '../../../assets/images/logos/woocommerce.svg'
import AmazonLogo from '../../../assets/images/logos/amazon.svg'

export interface CreateOrgPlanSelection {
  product: string
  interval: 'month' | 'year'
}

interface CreateOrgWizardResume {
  step: 'integrations'
  name: string
  plan: CreateOrgPlanSelection
}

function readCreateOrgWizardResume(): CreateOrgWizardResume | null {
  try {
    const raw = sessionStorage.getItem(CREATE_ORG_WIZARD_RESUME_KEY)
    if (!raw) return null
    sessionStorage.removeItem(CREATE_ORG_WIZARD_RESUME_KEY)
    const data = JSON.parse(raw) as CreateOrgWizardResume
    if (data?.step === 'integrations' && data.name) return data
  } catch {
    /* ignore */
  }
  return null
}

function persistCreateOrgWizardResume(name: string, plan: CreateOrgPlanSelection) {
  sessionStorage.setItem(
    CREATE_ORG_WIZARD_RESUME_KEY,
    JSON.stringify({ step: 'integrations', name, plan } satisfies CreateOrgWizardResume)
  )
}

function clearCreateOrgWizardResume() {
  try {
    sessionStorage.removeItem(CREATE_ORG_WIZARD_RESUME_KEY)
  } catch {
    /* ignore */
  }
}

interface CreateOrganizationWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type WizardStep = 'name' | 'plan' | 'checkout' | 'integrations'

function buildWizardSteps(planProduct: string): { id: WizardStep; label: string }[] {
  const steps: { id: WizardStep; label: string }[] = [
    { id: 'name', label: 'Name' },
    { id: 'plan', label: 'Plan' },
  ]
  if (planProduct !== 'Free') {
    steps.push({ id: 'checkout', label: 'Payment' })
  }
  steps.push({ id: 'integrations', label: 'Stores' })
  return steps
}

const STORE_INTEGRATIONS = [
  { id: 'shopify', label: 'Shopify', logo: ShopifyLogo },
  { id: 'bigcommerce', label: 'BigCommerce', logo: BigcommerceLogo },
  { id: 'woocommerce', label: 'WooCommerce', logo: WoocommerceLogo },
  { id: 'amazon', label: 'Amazon', logo: AmazonLogo },
] as const

const selectablePlans = planOptions(null).filter(
  (o) => !o.hidden && ['Free', 'Entrepreneur', 'Business'].includes(o.product)
)

function WizardStepIndicator({
  steps,
  currentIndex,
}: {
  steps: { id: WizardStep; label: string }[]
  currentIndex: number
}) {
  return (
    <div className="tw-mb-6 tw-flex tw-items-center tw-gap-2">
      {steps.map((s, i) => {
        const done = i < currentIndex
        const active = i === currentIndex
        return (
          <React.Fragment key={s.id}>
            {i > 0 && (
              <div
                className={cn(
                  'tw-h-0.5 tw-flex-1 tw-rounded-full tw-transition-colors',
                  done ? 'tw-bg-blue-500' : 'tw-bg-slate-200'
                )}
              />
            )}
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
              <div
                className={cn(
                  'tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-text-xs tw-font-semibold tw-transition-all',
                  done && 'tw-bg-blue-600 tw-text-white',
                  active && 'tw-bg-blue-600 tw-text-white tw-ring-4 tw-ring-blue-100',
                  !done && !active && 'tw-bg-slate-100 tw-text-slate-500'
                )}
              >
                {done ? <Check className="tw-h-4 tw-w-4" /> : i + 1}
              </div>
              <span
                className={cn(
                  'tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wide',
                  active ? 'tw-text-blue-700' : 'tw-text-slate-500'
                )}
              >
                {s.label}
              </span>
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default function CreateOrganizationWizard({
  open,
  onOpenChange,
}: CreateOrganizationWizardProps) {
  const dispatch = useDispatch()
  const rootUser = useSelector(selectRootUser)
  const rootAccounts = useSelector(selectRootAccounts)
  const businessPrice =
    useSelector(
      (state: { plan?: { subscription?: { plan_status?: { businessPrice?: number } } } }) =>
        state.plan?.subscription?.plan_status?.businessPrice
    ) ?? 100
  const billingChannel = useSelector(
    (state: { plan?: { billingChannel?: string | null } }) => state.plan?.billingChannel
  )
  const isShopifyBilling = billingChannel === 'shopify'

  const [step, setStep] = useState<WizardStep>('name')
  const [name, setName] = useState('')
  const [contextLoading, setContextLoading] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<CreateOrgPlanSelection>({
    product: 'Free',
    interval: 'month',
  })
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const wizardSteps = useMemo(
    () => buildWizardSteps(selectedPlan.product),
    [selectedPlan.product]
  )
  const requiresCheckout = selectedPlan.product !== 'Free'

  useEffect(() => {
    if (isShopifyBilling && selectedPlan.product !== 'Free') {
      setSelectedPlan({ product: 'Free', interval: 'month' })
    }
  }, [isShopifyBilling, selectedPlan.product])

  useEffect(() => {
    if (!open) return
    const resume = readCreateOrgWizardResume()
    if (resume) {
      setName(resume.name)
      setSelectedPlan(resume.plan)
      setStep('integrations')
      setCreateError(null)
      setCreating(false)
      return
    }
    let cancelled = false
    const load = async () => {
      setContextLoading(true)
      setCreateError(null)
      try {
        await dispatch(provisionOrganizationTenancy() as never)
      } catch {
        if (!cancelled) setCreateError('Could not load organization setup')
      } finally {
        if (!cancelled) setContextLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [open, dispatch])

  const stepIndex = wizardSteps.findIndex((s) => s.id === step)
  const stepMeta = wizardSteps[stepIndex] || wizardSteps[0]

  useEffect(() => {
    if (!wizardSteps.some((s) => s.id === step)) {
      setStep(wizardSteps[Math.min(1, wizardSteps.length - 1)]?.id ?? 'name')
    }
  }, [wizardSteps, step])

  const resetWizard = () => {
    setStep('name')
    setName('')
    setSelectedPlan({ product: 'Free', interval: 'month' })
    setCreating(false)
    setCreateError(null)
    setContextLoading(false)
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      resetWizard()
    }
    onOpenChange(next)
  }

  const afterOrgCreated = () => {
    persistCreateOrgWizardResume(name.trim(), selectedPlan)
    window.location.reload()
  }

  const createOrg = async (stripeToken?: string): Promise<boolean> => {
    const trimmed = name.trim()
    if (!trimmed) return false
    if (requiresCheckout && !stripeToken) {
      setCreateError('Payment is required for this plan')
      return false
    }
    setCreating(true)
    setCreateError(null)
    let succeeded = false
    try {
      const result = await dispatch(
        createOrganization({
          name: trimmed,
          plan: selectedPlan,
          ...(stripeToken ? { payment: { stripe_token: stripeToken } } : {}),
          ...(selectedPlan.product === 'Business' ? { business_price: businessPrice } : {}),
        }) as never
      )
      if (createOrganization.fulfilled.match(result)) {
        sendGtmPixel({
          email: rootUser?.email,
          userId: rootUser?.id,
          orgName: trimmed,
          plan: selectedPlan.product,
          storeAccount: pickPrimaryStoreAccount(rootAccounts),
        }, 'afftrack_org_created')
        setTimeout(afterOrgCreated, 500)
        succeeded = true
        return true
      }
      if (createOrganization.rejected.match(result)) {
        setCreateError((result.payload as string) || 'Failed to create organization')
      }
      return false
    } finally {
      if (!succeeded) {
        setCreating(false)
      }
    }
  }

  const handleNext = async () => {
    if (step === 'name') {
      if (!name.trim()) return
      setStep('plan')
      return
    }
    if (step === 'plan') {
      if (isShopifyBilling && selectedPlan.product !== 'Free') {
        setCreateError(
          'Paid organization plans require Stripe billing. Your account uses Shopify billing.'
        )
        return
      }
      if (requiresCheckout) {
        setStep('checkout')
        setCreateError(null)
        return
      }
      await createOrg()
      return
    }
  }

  const handleCheckoutSubmit = async (stripeToken: string) => {
    await createOrg(stripeToken)
  }

  const handleBack = () => {
    const prev = wizardSteps[stepIndex - 1]?.id
    if (prev) setStep(prev)
  }

  const finishWizard = (goToIntegrations: boolean) => {
    clearCreateOrgWizardResume()
    handleOpenChange(false)
    void dispatch(fetchOrganizations() as never)
    if (goToIntegrations) {
      window.location.assign('/integrations')
    }
  }

  const selectedPlanOption = useMemo(
    () => selectablePlans.find((p) => p.product === selectedPlan.product),
    [selectedPlan.product]
  )

  const stepSubtitle =
    step === 'name'
      ? 'Give your team a workspace with its own billing and stores.'
      : step === 'plan'
        ? isShopifyBilling
          ? 'Your account uses Shopify billing — new organizations start on the Free plan. Upgrade from My Plan after creation.'
          : requiresCheckout
            ? 'Choose a plan — paid plans require payment before your organization is created.'
            : 'Start on Free — upgrade anytime from Billing.'
        : step === 'checkout'
          ? 'Enter payment details to subscribe this organization before it is created.'
          : 'Optional — connect a storefront now or skip and do it later.'

  const entrepreneurPrice =
    selectablePlans.find((p) => p.product === 'Entrepreneur')?.priceMonth ?? 20

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          'tw-flex tw-max-h-[min(92vh,900px)] tw-flex-col tw-gap-0 tw-overflow-hidden tw-p-0 sm:tw-rounded-xl',
          'max-sm:tw-top-[max(1rem,env(safe-area-inset-top,0px))] max-sm:tw-translate-y-0',
          step === 'plan' || step === 'checkout' ? 'tw-max-w-4xl' : 'tw-max-w-lg'
        )}
      >
        <div className="tw-shrink-0 tw-border-b tw-border-slate-100 tw-bg-gradient-to-br tw-from-slate-50 tw-via-white tw-to-blue-50/40 tw-px-6 tw-pt-6 tw-pb-4">
          <DialogHeader className="tw-space-y-3 tw-text-left">
            <div className="tw-flex tw-items-start tw-gap-3">
              <div className="tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-blue-600 tw-text-white tw-shadow-md tw-shadow-blue-200">
                <Building2 className="tw-h-5 tw-w-5" />
              </div>
              <div className="tw-min-w-0 tw-flex-1 tw-pr-6">
                <DialogTitle className="tw-text-xl tw-font-semibold tw-tracking-tight tw-text-slate-900">
                  Create organization
                </DialogTitle>
                <p className="tw-mt-1 tw-text-sm tw-text-slate-600">
                  Step {stepIndex + 1} of {wizardSteps.length} · {stepMeta?.label}
                </p>
              </div>
            </div>
            <p className="tw-text-sm tw-leading-relaxed tw-text-slate-600">{stepSubtitle}</p>
          </DialogHeader>
          <WizardStepIndicator steps={wizardSteps} currentIndex={stepIndex} />
        </div>

        <div
          className={cn(
            'tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-6 tw-py-5',
            step === 'plan' && 'tw-overflow-x-auto'
          )}
        >
          {contextLoading && step !== 'integrations' && (
            <p className="tw-text-sm tw-text-slate-500">Loading…</p>
          )}

          {step === 'name' && (
            <div className="tw-space-y-2">
              <label
                htmlFor="org-name"
                className="tw-text-sm tw-font-medium tw-text-slate-800"
              >
                Organization name
              </label>
              <Input
                id="org-name"
                type="text"
                className="tw-h-11 tw-border-slate-200 tw-text-base focus-visible:tw-ring-blue-200"
                placeholder="e.g. Payhelm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && name.trim()) void handleNext()
                }}
                autoFocus
              />
              <p className="tw-text-xs tw-text-slate-500">
                You can rename this later from My Team.
              </p>
            </div>
          )}

          {step === 'plan' && (
            <div id="create-org-wizard" className="tw--mx-1">
              {isShopifyBilling && (
                <p className="tw-mb-3 tw-rounded-md tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-sm tw-text-amber-900">
                  Your account uses Shopify billing, so new organizations start on the Free plan.
                  After creation, open My Plan to upgrade this organization with the same Shopify
                  approval flow as your personal billing.
                </p>
              )}
              <Row className="pricing-cards-row tw-mx-0">
                {selectablePlans.map((option) => {
                  const planDisabled = isShopifyBilling && option.product !== 'Free'
                  return (
                    <PricingPlanCard
                      key={option.product}
                      colClassName={cn(planDisabled && 'tw-opacity-50 tw-pointer-events-none')}
                      option={option}
                      selectionMode
                      isSelected={selectedPlan.product === option.product}
                      isBusiness={option.product === 'Business'}
                      businessPrice={businessPrice}
                      hideDetailsLink
                      onClick={() => {
                        if (planDisabled) return
                        setSelectedPlan({ product: option.product, interval: 'month' })
                      }}
                    />
                  )
                })}
              </Row>
              {createError && (
                <p className="tw-mt-3 tw-rounded-md tw-bg-red-50 tw-px-3 tw-py-2 tw-text-sm tw-text-red-700">
                  {createError}
                </p>
              )}
            </div>
          )}

          {step === 'checkout' && (
            <CreateOrganizationCheckout
              plan={selectedPlan}
              businessPrice={businessPrice}
              entrepreneurPrice={entrepreneurPrice}
              submitting={creating}
              error={createError}
              onSubmit={(token) => void handleCheckoutSubmit(token)}
            />
          )}

          {step === 'integrations' && (
            <div className="tw-space-y-4">
              <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-emerald-200 tw-bg-emerald-50/80 tw-px-3 tw-py-2.5 tw-text-sm tw-text-emerald-900">
                <Sparkles className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-emerald-600" />
                <span>
                  <span className="tw-font-semibold">{name.trim()}</span> is ready on the{' '}
                  <span className="tw-font-semibold">{selectedPlanOption?.product}</span> plan.
                </span>
              </div>
              <p className="tw-text-sm tw-text-slate-600">
                Connect a store to start syncing orders and analytics.
              </p>
              <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                {STORE_INTEGRATIONS.map(({ id, label, logo }) => (
                  <button
                    key={id}
                    type="button"
                    className="tw-group tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-3 tw-text-left tw-shadow-sm tw-transition-all hover:tw-border-blue-300 hover:tw-shadow-md hover:tw-ring-1 hover:tw-ring-blue-100"
                    onClick={() => linkFromChannel(id)}
                  >
                    <img
                      src={logo}
                      alt=""
                      className="tw-h-8 tw-w-8 tw-object-contain tw-transition-transform group-hover:tw-scale-105"
                    />
                    <span className="tw-text-sm tw-font-semibold tw-text-slate-800">{label}</span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-border-dashed tw-border-slate-300 tw-py-2.5 tw-text-sm tw-font-medium tw-text-slate-700 tw-transition-colors hover:tw-border-slate-400 hover:tw-bg-slate-50"
                onClick={() => finishWizard(true)}
              >
                <Link2 className="tw-h-4 tw-w-4" />
                Open all integrations
                <ExternalLink className="tw-h-3.5 tw-w-3.5 tw-opacity-60" />
              </button>
            </div>
          )}
        </div>

        <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-gap-2 tw-border-t tw-border-slate-100 tw-bg-slate-50/60 tw-px-6 tw-py-4">
          <div>
            {step !== 'name' ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="tw-gap-1 tw-text-slate-600"
                onClick={handleBack}
                disabled={creating}
              >
                <ChevronLeft className="tw-h-4 tw-w-4" />
                Back
              </Button>
            ) : (
              <span />
            )}
          </div>
          <div className="tw-flex tw-gap-2">
            {step === 'integrations' ? (
              <Button type="button" variant="secondary" onClick={() => finishWizard(false)}>
                Skip for now
              </Button>
            ) : step === 'checkout' ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => handleOpenChange(false)}
                disabled={creating}
              >
                Cancel
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => handleOpenChange(false)}
                  disabled={creating}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="tw-gap-1"
                  onClick={() => void handleNext()}
                  disabled={
                    creating ||
                    contextLoading ||
                    (step === 'name' && !name.trim()) ||
                    (step === 'plan' && !selectedPlan.product)
                  }
                >
                  {creating
                    ? 'Creating…'
                    : step === 'plan'
                      ? requiresCheckout
                        ? 'Continue to payment'
                        : 'Create organization'
                      : 'Next'}
                  {!creating && step === 'name' && <ChevronRight className="tw-h-4 tw-w-4" />}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
